import { createFourierModel } from "./model";
import { samplePath, type Cubic, type Outline } from "./math/samplePath";
import { koiSource } from "./shapes/koi";

export const KOI_DURATION_SECONDS = 15;

// The four connected outer strokes form one closed contour for the familiar
// 1 / 5 / 20 / 256-circle progression. Every remaining stroke is traced by a
// separate Fourier pen afterward.
const outerIds = ["body-top", "tail-outline", "body-underside", "body-belly"];
const outerStrokes = outerIds.map((id) => {
  const stroke = koiSource.find((item) => item.id === id);
  if (!stroke) throw new Error(`Missing koi outline stroke: ${id}`);
  return stroke;
});

const parseCubics = (path: string): Cubic[] => {
  const tokens = path.match(/[MC]|-?\d+(?:\.\d+)?/g) ?? [];
  let index = 0;
  const readPoint = () => ({ x: Number(tokens[index++]), y: Number(tokens[index++]) });
  if (tokens[index++] !== "M") throw new Error("Koi outline must start with M");
  let from = readPoint();
  const curves: Cubic[] = [];
  while (index < tokens.length) {
    if (tokens[index++] !== "C") throw new Error("Koi outline only supports cubic curves");
    const c1 = readPoint();
    const c2 = readPoint();
    const to = readPoint();
    curves.push({ from, c1, c2, to });
    from = to;
  }
  return curves;
};

export const koiOutline: Outline = outerStrokes.flatMap((stroke) => parseCubics(stroke.path));
export const koiModel = createFourierModel(koiOutline, 1, [1, 5, 20, 256]);

// Match the source-to-model normalization for every detail pen and final pose.
const sourceSamples = samplePath(koiOutline);
export const koiSourceCenter = sourceSamples.reduce(
  (sum, point) => ({
    x: sum.x + point.x / sourceSamples.length,
    y: sum.y + point.y / sourceSamples.length,
  }),
  { x: 0, y: 0 },
);
const sourceExtent = Math.max(
  Math.max(...sourceSamples.map((point) => point.x)) - Math.min(...sourceSamples.map((point) => point.x)),
  Math.max(...sourceSamples.map((point) => point.y)) - Math.min(...sourceSamples.map((point) => point.y)),
);
export const koiArtworkScale = 2 / sourceExtent;
export const koiArtworkTransform = `scale(${koiArtworkScale}) translate(${-koiSourceCenter.x} ${-koiSourceCenter.y})`;
